import React, { useEffect, useState } from 'react';
import { Modal, Spin, Table, Typography, message } from 'antd';
import api from '../../services/api';
import { formatMatQty } from '../../config/matProductConstants';
import ProcessDetailContent from './ProcessDetailContent';

const { Text } = Typography;

const INBOUND_LINE_COLUMNS = [
  { title: '商品', dataIndex: 'product_name', ellipsis: true },
  { title: '单位', dataIndex: 'unit', width: 64, render: (v) => v || '-' },
  {
    title: '数量',
    dataIndex: 'qty',
    width: 90,
    align: 'right',
    render: (v) => formatMatQty(v)
  },
  {
    title: '单价',
    dataIndex: 'unit_price',
    width: 100,
    align: 'right',
    render: (v) => `¥${Number(v || 0).toFixed(2)}`
  },
  {
    title: '金额',
    dataIndex: 'amount',
    width: 100,
    align: 'right',
    render: (v) => `¥${Number(v || 0).toFixed(2)}`
  }
];

function MatInboundRelatedContent({ relatedInboundIds }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (!relatedInboundIds?.length) {
      setRows([]);
      setTotalAmount(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/materials/inbounds/related-summary', {
          params: { ids: relatedInboundIds.join(',') }
        });
        if (!cancelled && res.data.success) {
          const data = res.data.data || {};
          setRows(data.list || []);
          setTotalAmount(Number(data.total_amount || 0));
        }
      } catch (e) {
        if (!cancelled) {
          message.error(e.response?.data?.message || '加载关联入库单失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [relatedInboundIds]);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
        无法加载关联入库单
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px 24px' }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        点击行首展开图标可查看该入库单的物资明细
      </Text>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        expandable={{
          expandedRowRender: (record) => {
            const lines = record.lines || [];
            if (!lines.length) {
              return <Text type="secondary">暂无入库明细</Text>;
            }
            const lineTotal = lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
            return (
              <div style={{ margin: '4px 0 8px 48px' }}>
                <Table
                  rowKey={(line) => line.id}
                  size="small"
                  pagination={false}
                  dataSource={lines}
                  columns={INBOUND_LINE_COLUMNS}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        <Text type="secondary">本单明细合计</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong style={{ whiteSpace: 'nowrap' }}>¥{lineTotal.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
                {record.remark ? (
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    备注：{record.remark}
                  </Text>
                ) : null}
              </div>
            );
          },
          rowExpandable: (record) => (record.lines || []).length > 0
        }}
        columns={[
          { title: '入库单号', dataIndex: 'inbound_no', width: 140 },
          { title: '入库日期', dataIndex: 'inbound_date', width: 110, render: (v) => v || '-' },
          { title: '仓库', dataIndex: 'warehouse_name', ellipsis: true, render: (v) => v || '-' },
          { title: '供应商', dataIndex: 'supplier_name', ellipsis: true, render: (v) => v || '-' },
          {
            title: '明细行',
            width: 72,
            align: 'center',
            render: (_, record) => `${(record.lines || []).length} 行`
          },
          {
            title: '金额',
            dataIndex: 'total_amount',
            width: 120,
            align: 'right',
            render: (v) => <span style={{ whiteSpace: 'nowrap' }}>{`¥${Number(v || 0).toFixed(2)}`}</span>
          }
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={6}>合计</Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              <strong style={{ whiteSpace: 'nowrap' }}>¥{totalAmount.toFixed(2)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
}

export default function RelatedApprovalDetailModal({
  open,
  onClose,
  approvalId,
  bizType = 'business_trip_apply',
  relatedInboundIds = []
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const isMatInbound = bizType === 'mat_inbound';
  const inboundIds = (relatedInboundIds || []).filter(Boolean);

  useEffect(() => {
    if (!open || isMatInbound || !approvalId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/approval-center/initiated/${approvalId}`, {
          params: { biz_type: bizType }
        });
        if (!cancelled && res.data.success) {
          setDetail(res.data.data);
        }
      } catch (e) {
        if (!cancelled) {
          message.error(e.response?.data?.message || '加载关联审批单失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, approvalId, bizType, isMatInbound]);

  const process = detail?.process;
  const modalTitle = isMatInbound
    ? (inboundIds.length > 1 ? `关联入库单（${inboundIds.length} 张）` : '关联入库单')
    : (process?.process_no ? `关联审批单 - ${process.process_no}` : '关联审批单详情');

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMatInbound ? 920 : 800}
      destroyOnClose
      styles={{ body: { padding: 0, maxHeight: '75vh', overflow: 'auto' } }}
    >
      {isMatInbound ? (
        <MatInboundRelatedContent relatedInboundIds={inboundIds} />
      ) : (
        <>
          {loading && (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Spin />
            </div>
          )}
          {!loading && process && (
            <ProcessDetailContent
              process={process}
              processType={process.process_type}
              processTypeLabel={process.process_type_label}
              bizType={detail?.biz_type || bizType}
              bizId={process.id}
              commentApi="approval-center"
            />
          )}
          {!loading && !process && (
            <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
              无法加载关联审批单
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
